import { JourneyEditableState } from '@pushly/models/lib/enums/journeys'
import { getEditableState, JourneyEditableFields } from '@pushly/models/lib/utils/journey-editable-state'
import * as moment from 'moment-timezone'
import { StatusType } from '../../../enums/status-type'
import { isJourneyCompleted, Journey, LiveJourneyStatusName } from '../../../features/journeys/types/journey-api-response'

import type { IJourneySubscriberFcapRequest } from '@pushly/models/lib/structs/journeys/requests/request-interfaces'

export { getEditableState, JourneyEditableState }
export type { JourneyEditableFields }

type CalculatedStatusJourney = Pick<
    Journey,
    | 'date_start_utc'
    | 'date_start'
    | 'date_end_utc'
    | 'date_end'
    | 'status'
    | 'status_id'
    | 'date_completed_utc'
    | 'manually_completed'
>

export const getCalculatedStatus = (journey: CalculatedStatusJourney, format?: (status: string, journey: Pick<Journey, 'status'>) => string): string => {
    let computedStatus: string = StatusType.SCHEDULED.name

    const now = moment.utc()
    const dateStartValue = journey.date_start_utc || journey.date_start
    const dateStart = moment.tz(dateStartValue, 'utc')
    const dateEndValue = journey.date_end_utc || journey.date_end
    const dateEnd = !dateEndValue ? null : moment.tz(dateEndValue, 'utc')

    const editableState = getEditableState(journey)
    const editableStateIsCompleted = editableState === JourneyEditableState.COMPLETED

    const statusIsDraft = journey.status === StatusType.DRAFT.name
    const statusIsPaused = journey.status === StatusType.PAUSED.name
    const statusIsCancelled =
        journey.status === StatusType.CANCELLED.name || journey.status === StatusType.CANCELLING.name
    const statusIsCompleted =
        isJourneyCompleted(journey) || editableStateIsCompleted || (statusIsDraft && dateEnd && dateEnd <= now)

    if (statusIsCompleted) {
        computedStatus = StatusType.COMPLETED.name
    } else if (statusIsCancelled) {
        computedStatus = StatusType.CANCELLED.name
    } else if (statusIsDraft) {
        computedStatus = StatusType.DRAFT.name
    } else if (statusIsPaused) {
        // A paused journey sends nothing and admits nobody whatever its run dates say,
        // so neither SCHEDULED nor ACTIVE describes it.
        computedStatus = StatusType.PAUSED.name
    } else if (dateStart <= now) {
        computedStatus = StatusType.ACTIVE.name
    }

    return !format ? computedStatus : format(computedStatus, journey)
}

/**
 * Whether a journey is published and still running its course — ACTIVE or PAUSED, and neither
 * completed nor past its end date. These are the journeys a person can pause or resume.
 */
export const isJourneyActiveOrPaused = (journey: CalculatedStatusJourney): boolean => {
    const hasLiveStatus = journey.status === StatusType.ACTIVE.name || journey.status === StatusType.PAUSED.name

    return hasLiveStatus && getCalculatedStatus(journey) !== StatusType.COMPLETED.name
}

/** Whether a journey has not been published yet: a saved draft, or one still being created. */
export const isJourneyUnpublished = (journey: Pick<Journey, 'status'>): boolean =>
    !journey.status || journey.status === StatusType.DRAFT.name

/** The status an unpublished journey takes when the builder publishes it. */
export const getJourneyStatusOnPublish = (journey: Pick<Journey, 'configuration'>): LiveJourneyStatusName =>
    journey.configuration?.status_on_publish === 'PAUSED' ? 'PAUSED' : 'ACTIVE'

/**
 * The status an edit moves a published journey to, or undefined when the edit leaves its status
 * alone. Only an ACTIVE or PAUSED journey can change status this way; for any other saved journey
 * the edited copy's status is ignored.
 */
export const getPublishedJourneyStatusChange = (
    savedJourney: CalculatedStatusJourney | undefined,
    editedJourney: Pick<Journey, 'status'>,
): LiveJourneyStatusName | undefined => {
    if (!savedJourney || !isJourneyActiveOrPaused(savedJourney)) {
        return undefined
    }

    const editedStatus = editedJourney.status
    if (editedStatus === savedJourney.status) {
        return undefined
    }

    return editedStatus === 'ACTIVE' || editedStatus === 'PAUSED' ? editedStatus : undefined
}

/** The builder's save button: a draft is published by it, with the status its configuration chose. */
export const getJourneyBuilderSubmitText = (
    journey: Pick<Journey, 'status' | 'configuration'>,
    builderHasChanged: boolean,
): string => {
    if (isJourneyUnpublished(journey)) {
        return getJourneyStatusOnPublish(journey) === 'PAUSED' ? 'Publish (paused)' : 'Publish'
    }

    return builderHasChanged ? 'Save Changes' : 'Save'
}

export const generateDefaultSubscriberFcap = (): IJourneySubscriberFcapRequest => ({
    criteria: {
        frequency: 1,
        window: 'days',
    },
    type: 'once_per_criteria',
})
